import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiCheck, FiMail, FiInfo, FiChevronRight, FiChevronLeft, FiPhone } from 'react-icons/fi';

const DeleteAccountPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [otherReason, setOtherReason] = useState('');

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const reasons = [
    'I no longer use this service',
    'Privacy concerns',
    'Found a better alternative',
    'Too many notifications',
    'Account created by mistake',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setStep(3);
      setIsSubmitted(true);
    }, 1500);
  };

  const resetForm = () => {
    setStep(1);
    setEmail('');
    setMobile('');
    setReason('');
    setFeedback('');
    setOtherReason('');
    setIsSubmitted(false);
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleReasonChange = (value) => {
    setReason(value);
    if (value !== 'Other') {
      setOtherReason('');
    }
  };

  const handleContinue = () => {
    if (email.trim() && mobile.trim()) {
      setStep(2);
    }
  };

  return (
    <div className="delete-account-page">
      {/* Progress Bar for Mobile */}
      {isMobile && !isSubmitted && (
        <div className="mobile-progress-bar">
          <div className="progress-fill" style={{ width: `${(step - 1) * 50}%` }}></div>
          <span>Step {step} of 2</span>
        </div>
      )}

      <main className="page-content">
        <div className="container">
          {/* Desktop Progress Steps */}
          {!isMobile && !isSubmitted && (
            <div className="progress-steps">
              {[1, 2, 3].map((stepNumber) => (
                <div
                  key={stepNumber}
                  className={`step ${step === stepNumber ? 'active' : ''} ${step > stepNumber ? 'completed' : ''}`}
                >
                  <div className="step-circle">{stepNumber}</div>
                  <span className="step-label">
                    {stepNumber === 1 && 'Verify Identity'}
                    {stepNumber === 2 && 'Confirm'}
                    {stepNumber === 3 && 'Complete'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Back Button for Mobile */}
          {isMobile && step > 1 && !isSubmitted && (
            <button onClick={goBack} className="mobile-back-btn">
              <FiChevronLeft /> Back
            </button>
          )}

          {/* Main Content Card */}
          <div className="card">
            <div className="card-header">
              <FiAlertTriangle className="header-icon" />
              <h1>Account Deletion Request</h1>
              <p className="subtitle">
                We're sorry to see you go. This process will permanently delete your account and all associated data.
              </p>
            </div>

            {isSubmitted ? (
              <div className="success-message">
                <FiCheck className="success-icon" />
                <h2>Account Deletion Request Received</h2>
                <p>
                  Your account deletion request has been submitted successfully. 
                  Our team will process your request within <strong>3-4 working days</strong>.
                </p>
                <div className="info-box">
                  <FiInfo />
                  <div>
                    <p><strong>What happens next:</strong></p>
                    <ul>
                      <li>Your account will be deactivated immediately</li>
                      <li>All personal data will be permanently deleted within 3-4 working days</li>
                      <li>You will lose access to all services immediately</li>
                      <li>Any active subscriptions will be canceled</li>
                    </ul>
                    <p style={{ marginTop: '12px', fontSize: '0.9rem' }}>
                      <strong>Note:</strong> This action cannot be undone. Once deleted, your account cannot be recovered.
                    </p>
                  </div>
                </div>
                <div className="success-actions">
                  <button onClick={resetForm} className="btn btn-secondary">
                    Submit Another Request
                  </button>
                  <a href="/" className="btn btn-primary">
                    Return to Home
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="deletion-form">
                {step === 1 && (
                  <div className="form-step">
                    <div className="form-group">
                      <label htmlFor="email">
                        <FiMail /> Email Address <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your registered email address"
                        required
                        autoComplete="email"
                      />
                      <div className="input-hint">
                        We need this to identify your account
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="mobile">
                        <FiPhone /> Mobile Number <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        id="mobile"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="Enter your registered mobile number"
                        required
                        autoComplete="tel"
                      />
                      <div className="input-hint">
                        We need this for verification purposes
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Reason for Leaving <span className="required">*</span></label>
                      <div className="reason-options">
                        {reasons.map((option) => (
                          <label key={option} className="reason-option">
                            <input
                              type="radio"
                              name="reason"
                              value={option}
                              checked={reason === option}
                              onChange={(e) => handleReasonChange(e.target.value)}
                              required
                            />
                            <span className="reason-text">{option}</span>
                            {option === 'Other' && reason === 'Other' && (
                              <div className="other-reason-input">
                                <input
                                  type="text"
                                  placeholder="Please specify your reason..."
                                  className="other-input"
                                  value={otherReason}
                                  onChange={(e) => {
                                    setOtherReason(e.target.value);
                                    setReason(`Other: ${e.target.value}`);
                                  }}
                                />
                              </div>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="feedback">
                        <FiInfo /> Additional Feedback (Optional)
                      </label>
                      <textarea
                        id="feedback"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Your feedback helps us improve our service..."
                        rows="3"
                      />
                      <div className="input-hint">
                        Share any suggestions or concerns you have
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        onClick={handleContinue}
                        className="btn btn-primary"
                        disabled={!email.trim() || !mobile.trim() || !reason}
                      >
                        Continue to Confirmation <FiChevronRight />
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="form-step">
                    <div className="warning-box">
                      <FiAlertTriangle />
                      <h3>⚠️ Important: Read Before Proceeding</h3>
                      <p>By deleting your account, you acknowledge and understand that:</p>
                      <ul>
                        <li><strong>This action is permanent and irreversible</strong></li>
                        <li><strong>All your personal data</strong> will be permanently deleted within 3-4 working days</li>
                        <li><strong>You will immediately lose access</strong> to all Krishidnya services</li>
                        <li><strong>Any active subscriptions or services</strong> will be terminated immediately</li>
                        <li><strong>Your account history, preferences, and saved data</strong> will be completely erased</li>
                        <li><strong>You will need to create a new account</strong> if you wish to use our services again</li>
                      </ul>
                    </div>

                    <div className="confirmation-details">
                      <h4>Account Details to be Deleted</h4>
                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{email}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Mobile:</span>
                          <span className="detail-value">{mobile}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Reason:</span>
                          <span className="detail-value">
                            {reason === 'Other' ? `Other: ${otherReason}` : reason}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="final-confirmation">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          required
                          className="confirmation-checkbox"
                        />
                        <span className="checkbox-text">
                          I understand that this action is permanent and cannot be undone. 
                          I confirm that I want to permanently delete my account and all associated data.
                        </span>
                      </label>
                    </div>

                    <div className="form-actions">
                      {!isMobile && (
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="btn btn-secondary"
                        >
                          <FiChevronLeft /> Back to Edit
                        </button>
                      )}
                      <button
                        type="submit"
                        className="btn btn-danger"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner"></span>
                            Processing Request...
                          </>
                        ) : (
                          'Permanently Delete My Account'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* FAQ Section */}
          {!isSubmitted && (
            <div className="faq-section">
              <h2>Frequently Asked Questions</h2>
              <div className="faq-grid">
                <div className="faq-item">
                  <h3>How long does account deletion take?</h3>
                  <p>Account deletion is processed within <strong>3-4 working days</strong>. Your account will be deactivated immediately upon request.</p>
                </div>
                <div className="faq-item">
                  <h3>Can I recover my account after deletion?</h3>
                  <p><strong>No, account deletion is permanent.</strong> Once deleted, your account and all associated data cannot be recovered.</p>
                </div>
                <div className="faq-item">
                  <h3>What data will be deleted?</h3>
                  <p>All personal information, account preferences, usage history, saved data, and transaction records will be permanently removed.</p>
                </div>
                <div className="faq-item">
                  <h3>Will my subscriptions be canceled?</h3>
                  <p>Yes, any active subscriptions or premium services will be canceled immediately upon account deletion.</p>
                </div>
                <div className="faq-item">
                  <h3>Can I use the same email/mobile again?</h3>
                  <p>Yes, you can create a new account with the same email or mobile number after your current account is deleted.</p>
                </div>
                <div className="faq-item">
                  <h3>What if I change my mind?</h3>
                  <p>You have until the deletion is processed (3-4 days) to contact support to cancel the deletion request.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <style jsx>{`
        .delete-account-page {
          min-height: calc(100vh - 140px);
          display: flex;
          flex-direction: column;
          background: #f9fafb;
          padding-top: 20px;
        }

        .page-content {
          flex: 1;
          padding: 20px 0 40px;
          background: #f9fafb;
        }

        .container {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
        }

        /* Mobile Progress Bar */
        .mobile-progress-bar {
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          height: 4px;
          background: #e5e7eb;
          z-index: 100;
        }

        .mobile-progress-bar .progress-fill {
          height: 100%;
          background: #10B981;
          transition: width 0.3s ease;
        }

        .mobile-progress-bar span {
          position: absolute;
          top: -25px;
          right: 20px;
          font-size: 0.8rem;
          color: #6b7280;
          font-weight: 500;
        }

        /* Mobile Back Button */
        .mobile-back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #6b7280;
          font-size: 0.95rem;
          padding: 10px 0;
          margin-bottom: 20px;
          cursor: pointer;
          font-weight: 500;
        }

        .mobile-back-btn:hover {
          color: #374151;
        }

        /* Desktop Progress Steps */
        .progress-steps {
          display: flex;
          justify-content: space-between;
          margin-bottom: 40px;
          position: relative;
        }

        .progress-steps::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 25px;
          right: 25px;
          height: 2px;
          background: #e5e7eb;
          z-index: 1;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e5e7eb;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          margin-bottom: 8px;
          transition: all 0.3s;
          border: 3px solid white;
        }

        .step.active .step-circle {
          background: #10B981;
          color: white;
          transform: scale(1.1);
        }

        .step.completed .step-circle {
          background: #059669;
          color: white;
        }

        .step-label {
          font-size: 0.9rem;
          color: #6b7280;
          font-weight: 500;
        }

        .step.active .step-label {
          color: #10B981;
          font-weight: 600;
        }

        /* Card Styles */
        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          padding: 40px;
          margin-bottom: 40px;
          border: 1px solid #e5e7eb;
        }

        .card-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .header-icon {
          font-size: 48px;
          color: #ef4444;
          margin-bottom: 20px;
        }

        .card-header h1 {
          color: #1f2937;
          font-size: 2rem;
          margin-bottom: 10px;
          line-height: 1.3;
        }

        .subtitle {
          color: #6b7280;
          font-size: 1.1rem;
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Form Styles */
        .deletion-form {
          margin-top: 30px;
        }

        .form-step {
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .form-group {
          margin-bottom: 28px;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #374151;
          font-weight: 600;
          margin-bottom: 10px;
          font-size: 1rem;
        }

        .required {
          color: #ef4444;
          margin-left: 4px;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s;
          background: #f9fafb;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #10B981;
          background: white;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .input-hint {
          color: #6b7280;
          font-size: 0.85rem;
          margin-top: 6px;
          margin-left: 4px;
        }

        .reason-options {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .reason-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          background: #f9fafb;
          flex-direction: column;
        }

        .reason-option:hover {
          border-color: #10B981;
          background: white;
        }

        .reason-option input[type="radio"] {
          accent-color: #10B981;
          transform: scale(1.2);
          margin-top: 2px;
        }

        .reason-text {
          flex: 1;
          color: #374151;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .other-reason-input {
          width: 100%;
          margin-top: 12px;
          padding-left: 24px;
        }

        .other-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.95rem;
        }

        .warning-box {
          background: #fef2f2;
          border: 2px solid #fecaca;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 30px;
        }

        .warning-box svg {
          color: #ef4444;
          font-size: 24px;
          margin-bottom: 16px;
        }

        .warning-box h3 {
          color: #991b1b;
          margin-bottom: 12px;
          font-size: 1.2rem;
        }

        .warning-box p {
          color: #7f1d1d;
          margin-bottom: 16px;
          font-weight: 500;
        }

        .warning-box ul {
          color: #7f1d1d;
          padding-left: 20px;
          line-height: 1.6;
        }

        .warning-box li {
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .confirmation-details {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 25px;
        }

        .confirmation-details h4 {
          color: #0369a1;
          margin-bottom: 16px;
          font-size: 1.1rem;
        }

        .details-grid {
          display: grid;
          gap: 12px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .detail-label {
          color: #374151;
          font-weight: 600;
          min-width: 80px;
        }

        .detail-value {
          color: #1f2937;
          word-break: break-word;
        }

        .final-confirmation {
          margin: 30px 0;
          padding: 20px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
        }

        .confirmation-checkbox {
          accent-color: #ef4444;
          transform: scale(1.2);
          margin-top: 4px;
          flex-shrink: 0;
        }

        .checkbox-text {
          color: #1f2937;
          line-height: 1.5;
          font-weight: 500;
        }

        .form-actions {
          display: flex;
          gap: 16px;
          justify-content: flex-end;
          margin-top: 40px;
        }

        /* Button Styles */
        .btn {
          padding: 14px 28px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 48px;
        }

        .btn-primary {
          background: #10B981;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .btn-secondary {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #d1d5db;
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .btn-danger {
          background: #ef4444;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s ease-in-out infinite;
          margin-right: 8px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Success Message */
        .success-message {
          text-align: center;
          padding: 40px 20px;
          animation: fadeIn 0.5s ease-out;
        }

        .success-icon {
          font-size: 64px;
          color: #10B981;
          margin-bottom: 24px;
        }

        .success-message h2 {
          color: #059669;
          margin-bottom: 16px;
          font-size: 1.8rem;
          line-height: 1.3;
        }

        .success-message p {
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 32px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          font-size: 1.05rem;
        }

        .info-box {
          background: #f0f9ff;
          border: 2px solid #bae6fd;
          border-radius: 8px;
          padding: 24px;
          margin: 30px auto;
          max-width: 600px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          text-align: left;
        }

        .info-box svg {
          color: #0369a1;
          font-size: 24px;
          flex-shrink: 0;
          margin-top: 4px;
        }

        .info-box p {
          color: #0c4a6e;
          margin: 0;
          line-height: 1.6;
        }

        .info-box ul {
          color: #0c4a6e;
          padding-left: 20px;
          margin: 12px 0;
        }

        .info-box li {
          margin-bottom: 6px;
          line-height: 1.5;
        }

        .success-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-top: 40px;
          flex-wrap: wrap;
        }

        /* FAQ Section */
        .faq-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 1px solid #e5e7eb;
        }

        .faq-section h2 {
          color: #1f2937;
          font-size: 1.8rem;
          text-align: center;
          margin-bottom: 40px;
        }

        .faq-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .faq-item {
          background: white;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          border: 1px solid #e5e7eb;
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .faq-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .faq-item h3 {
          color: #374151;
          font-size: 1.1rem;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .faq-item p {
          color: #6b7280;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .delete-account-page {
            min-height: calc(100vh - 120px);
            padding-top: 0;
          }

          .mobile-progress-bar {
            top: 60px;
          }

          .page-content {
            padding: 10px 0 30px;
          }

          .container {
            padding: 0 16px;
          }

          .card {
            padding: 25px 20px;
            margin-bottom: 30px;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
          }

          .card-header h1 {
            font-size: 1.6rem;
            margin-bottom: 8px;
          }

          .subtitle {
            font-size: 1rem;
            line-height: 1.5;
          }

          .header-icon {
            font-size: 40px;
            margin-bottom: 16px;
          }

          .progress-steps {
            margin-bottom: 30px;
          }

          .step-circle {
            width: 36px;
            height: 36px;
            font-size: 0.9rem;
          }

          .step-label {
            font-size: 0.8rem;
          }

          .form-group {
            margin-bottom: 24px;
          }

          .form-group label {
            font-size: 0.95rem;
            margin-bottom: 8px;
          }

          .form-group input,
          .form-group textarea {
            padding: 12px 14px;
            font-size: 0.95rem;
          }

          .btn {
            padding: 14px 20px;
            font-size: 0.95rem;
            width: 100%;
            min-height: 44px;
          }

          .form-actions {
            flex-direction: column;
            gap: 12px;
            margin-top: 30px;
          }

          .faq-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .faq-section {
            margin-top: 40px;
            padding-top: 30px;
          }

          .faq-section h2 {
            font-size: 1.5rem;
            margin-bottom: 30px;
          }

          .faq-item {
            padding: 20px;
          }

          .warning-box,
          .info-box {
            padding: 20px;
            margin: 25px auto;
          }

          .success-message {
            padding: 30px 16px;
          }

          .success-icon {
            font-size: 48px;
            margin-bottom: 20px;
          }

          .success-message h2 {
            font-size: 1.5rem;
          }

          .success-message p {
            font-size: 1rem;
          }

          .success-actions {
            flex-direction: column;
            gap: 12px;
          }

          .reason-option {
            padding: 14px;
          }

          .reason-text {
            font-size: 0.95rem;
          }

          .confirmation-details {
            padding: 16px;
          }

          .final-confirmation {
            padding: 16px;
            margin: 20px 0;
          }

          .checkbox-text {
            font-size: 0.95rem;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 12px;
          }

          .card-header h1 {
            font-size: 1.4rem;
          }

          .success-message h2 {
            font-size: 1.3rem;
          }

          .warning-box h3 {
            font-size: 1.1rem;
          }

          .faq-item h3 {
            font-size: 1rem;
          }

          .faq-item p {
            font-size: 0.9rem;
          }

          .detail-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .detail-label {
            min-width: auto;
          }
        }

        /* Extra small devices */
        @media (max-width: 360px) {
          .card {
            padding: 20px 16px;
          }

          .btn {
            padding: 12px 16px;
            font-size: 0.9rem;
          }

          .card-header h1 {
            font-size: 1.3rem;
          }

          .subtitle {
            font-size: 0.95rem;
          }

          .warning-box,
          .info-box {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default DeleteAccountPage; 